/**
 * Single source of truth for reading prices on the admin frontend.
 *
 * The backend (`getDisplayPrice`) attaches a structured `price` object to every
 * article it returns:
 *
 *   { currentPrice, basePrice, originalPrice, inDiscount, discountPercentage, currency }
 *
 * This helper normalizes anything the frontend might receive (article object,
 * full product, raw search scalar, legacy shapes) into that same canonical
 * shape so display code never branches.
 */

const EMPTY = Object.freeze({
  currentPrice: null,
  basePrice: null,
  inDiscount: false,
  discountPercentage: null,
  currency: 'CHF',
});

const normalize = (p) => ({
  currentPrice: p?.currentPrice ?? null,
  basePrice: p?.basePrice ?? p?.originalPrice ?? null,
  inDiscount: !!p?.inDiscount,
  discountPercentage: p?.discountPercentage ?? null,
  currency: p?.currency || 'CHF',
});

const isStructuredPrice = (v) =>
  v && typeof v === 'object' && ('currentPrice' in v || 'basePrice' in v || 'originalPrice' in v);

/**
 * Accepts an article, a product, a price object, or a scalar number.
 * Always returns the canonical shape — never null.
 */
export const getPriceShape = (input) => {
  if (!input) return { ...EMPTY };

  // Already a structured price object
  if (isStructuredPrice(input)) return normalize(input);

  // Article with structured `price` (the common case from new backend)
  if (isStructuredPrice(input.price)) return normalize(input.price);

  // Product whose first article has a structured `price`
  const firstArticle = Array.isArray(input.articles) ? input.articles[0] : null;
  if (isStructuredPrice(firstArticle?.price)) return normalize(firstArticle.price);

  // Search-style scalar — `price` is just a number (legacy / alternate endpoint)
  if (typeof input.price === 'number') {
    return { ...EMPTY, currentPrice: input.price, basePrice: input.price };
  }
  if (firstArticle && typeof firstArticle.price === 'number') {
    return { ...EMPTY, currentPrice: firstArticle.price, basePrice: firstArticle.price };
  }

  return { ...EMPTY };
};

// Number-only Swiss formatting, e.g. "1'234.50" (no currency word). Kept for
// callers that render the currency separately (e.g. PriceDisplay).
export const formatCHF = (n) =>
  Number(n).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CHF_FORMATTER = new Intl.NumberFormat('de-CH', {
  style: 'currency',
  currency: 'CHF',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Canonical money string for the whole admin, e.g. "CHF 1'234.50".
 * Use this EVERYWHERE a CHF amount is displayed so symbol placement (prefix)
 * and Swiss digit grouping are consistent. Replaces the ad-hoc mix of
 * `${n.toFixed(2)} CHF` and `CHF ${n.toFixed(2)}` that had differed across screens.
 */
export const formatMoneyCHF = (n) => CHF_FORMATTER.format(Number(n) || 0);

export const PRICE_EMPTY = EMPTY;
